import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Save, RefreshCw } from 'lucide-react';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import { validateFeeTiers } from '../utils/transactionFees';
import useLatestRequest from '../hooks/useLatestRequest';

const TransactionFees = () => {
  const [feeTiers, setFeeTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Only the latest load may fill the tier inputs: StrictMode runs the mount effect
  // twice, and initialize / reset-to-default load again, so a late response would
  // otherwise overwrite the tiers the admin has edited since.
  const beginRequest = useLatestRequest();

  const fetchFeeConfig = useCallback(async () => {
    const isCurrent = beginRequest();
    try {
      setLoading(true);
      const res = await api.get('/api/transaction-fees');
      if (!isCurrent()) return;
      const config = res.data.data.map(row => ({
        id: row.id,
        minPkr: Number.parseFloat(row.min_pkr),
        maxPkr: row.max_pkr === null ? 'Infinity' : Number.parseFloat(row.max_pkr),
        usdFee: Number.parseFloat(row.usd_fee)
      }));
      setFeeTiers(config);
    } catch (err) {
      if (!isCurrent()) return;
      console.error('Failed to fetch fee config:', err);
      toast.error('Failed to load transaction fee configuration');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [beginRequest]);

  useEffect(() => {
    fetchFeeConfig();
  }, [fetchFeeConfig]);

  const initializeConfig = async () => {
    try {
      setSaving(true);
      await api.post('/api/transaction-fees/initialize');
      toast.success('Transaction fee configuration initialized successfully');
      await fetchFeeConfig(); // Refresh the data
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to initialize configuration';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const addTier = () => {
    const newTier = {
      id: Date.now(), // Temporary ID for new tiers
      minPkr: 0,
      maxPkr: 100000,
      usdFee: 0
    };
    setFeeTiers([...feeTiers, newTier]);
  };

  const removeTier = (index) => {
    setFeeTiers(feeTiers.filter((_, i) => i !== index));
  };

  const updateTier = (index, field, value) => {
    const updatedTiers = [...feeTiers];
    updatedTiers[index] = {
      ...updatedTiers[index],
      [field]: field === 'maxPkr' && value === 'Infinity' ? 'Infinity' : Number.parseFloat(value) || 0
    };
    setFeeTiers(updatedTiers);
  };

  const saveConfig = async () => {
    // Validate the configuration
    const validation = validateFeeTiers(feeTiers);
    if (!validation.isValid) {
      setError(validation.errors.join(', '));
      toast.error('Please fix validation errors');
      return;
    }

    try {
      setSaving(true);
      setError('');
      
      // Convert Infinity to null for backend
      const configForBackend = feeTiers.map(tier => ({
        minPkr: tier.minPkr,
        maxPkr: tier.maxPkr === 'Infinity' ? null : tier.maxPkr,
        usdFee: tier.usdFee
      }));

      await api.put('/api/transaction-fees', { feeTiers: configForBackend });
      toast.success('Transaction fee configuration saved successfully');
      await fetchFeeConfig(); // Refresh the data
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to save configuration';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    const isCurrent = beginRequest();
    try {
      setLoading(true);
      const res = await api.get('/api/transaction-fees/default');
      if (!isCurrent()) return;
      const defaultConfig = res.data.data.map((tier, index) => ({
        id: index,
        minPkr: tier.minPkr,
        maxPkr: tier.maxPkr === Infinity ? 'Infinity' : tier.maxPkr,
        usdFee: tier.usdFee
      }));
      setFeeTiers(defaultConfig);
      toast.success('Reset to default configuration');
    } catch (err) {
      if (isCurrent()) toast.error('Failed to reset to default');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const formatPkrRange = (min, max) => {
    if (max === 'Infinity' || max === Infinity) {
      return `PKR ${min.toLocaleString()}+`;
    }
    return `PKR ${min.toLocaleString()} - ${max.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" data-testid="loading-spinner"></div>
      </div>
    );
  }

  // Show initialization option if no configuration exists
  if (feeTiers.length === 0) {
    return (
      <div className="w-full mx-auto px-0 py-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Transaction Fee Configuration</h2>
        </div>

        <div className="bg-white rounded-lg shadow-soft p-8 text-center">
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">No Configuration Found</h3>
            <p className="text-gray-600">
              No transaction fee configuration has been set up yet. 
              You can initialize the system with default values or create a custom configuration.
            </p>
          </div>

          <div className="flex justify-center gap-4">
            <button
              onClick={initializeConfig}
              disabled={saving}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg flex items-center gap-2 hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Initializing...' : 'Initialize with Defaults'}
            </button>
            <button
              onClick={resetToDefault}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg flex items-center gap-2 hover:bg-gray-300"
            >
              <RefreshCw className="h-4 w-4" />
              Load Defaults (Preview)
            </button>
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600">{error}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto px-0 py-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Transaction Fee Configuration</h2>
        <div className="flex gap-2">
          <button
            onClick={resetToDefault}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg flex items-center gap-2 hover:bg-gray-300"
          >
            <RefreshCw className="h-4 w-4" />
            Reset to Default
          </button>
          <button
            data-testid="transaction-fees-submit"
            onClick={saveConfig}
            disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg flex items-center gap-2 hover:bg-primary-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-soft p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2">USD Transaction Fee Tiers</h3>
          <p className="text-gray-600 text-sm">
            Configure transaction fees for USD payouts based on calculated PKR salary ranges. 
            Fees are deducted from the USD amount to be paid.
          </p>
        </div>

        <div className="space-y-4">
          {feeTiers.map((tier, index) => (
            <div key={tier.id} data-testid={`transaction-fees-row-${tier.id}`} className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tier {index + 1}
                </label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Min PKR</label>
                    <input
                      type="number"
                      value={tier.minPkr}
                      onChange={(e) => updateTier(index, 'minPkr', e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Max PKR</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        data-testid="max-pkr"
                        value={tier.maxPkr === 'Infinity' || tier.maxPkr === Infinity ? '' : tier.maxPkr}
                        onChange={(e) => updateTier(index, 'maxPkr', e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        disabled={tier.maxPkr === 'Infinity' || tier.maxPkr === Infinity}
                        placeholder={tier.maxPkr === 'Infinity' || tier.maxPkr === Infinity ? 'No limit' : 'e.g. 250000'}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
                        min="0"
                        aria-label="Max PKR"
                      />
                      <label className="flex items-center gap-1.5 whitespace-nowrap text-xs text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tier.maxPkr === 'Infinity' || tier.maxPkr === Infinity}
                          onChange={(e) => {
                            const wasNoLimit = tier.maxPkr === 'Infinity' || tier.maxPkr === Infinity;
                            const nextMaxPkr = e.target.checked ? 'Infinity' : (wasNoLimit ? 100000 : tier.maxPkr);
                            updateTier(index, 'maxPkr', nextMaxPkr);
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          aria-label="No upper limit"
                        />
                        No limit
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">USD Fee</label>
                    <input
                      type="number"
                      value={tier.usdFee}
                      onChange={(e) => updateTier(index, 'usdFee', e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                <div className="mt-2 text-sm text-gray-500">
                  Range: {formatPkrRange(tier.minPkr, tier.maxPkr)} | Fee: ${tier.usdFee}
                </div>
              </div>
              <button
                data-testid="transaction-fees-delete"
                onClick={() => removeTier(index)}
                className="p-2 text-red-600 hover:bg-red-50 rounded"
                title="Remove tier"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        <button
          data-testid="transaction-fees-add-btn"
          onClick={addTier}
          className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg flex items-center gap-2 hover:bg-gray-200"
        >
          <Plus className="h-4 w-4" />
          Add Tier
        </button>
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">How it works:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Transaction fees are calculated based on the PKR salary amount</li>
          <li>• Fees are deducted from the gross USD amount before payment</li>
          <li>• Tiers should not overlap - each PKR range should be unique</li>
          <li>• The last tier can be set to "No Limit" to cover all amounts above the minimum</li>
          <li>• Changes take effect immediately for new salary sheet reports</li>
        </ul>
      </div>
    </div>
  );
};

export default TransactionFees; 